import { FC, SyntheticEvent, useEffect, useMemo, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Alert from "@mui/material/Alert";
import { Box, Stack, Tooltip, Typography } from "@mui/material";
import {
  FoodItem,
  FoodItemSwapUsagesReport,
  FoodUsageAsIngredient,
  MenuLoopUsage,
  MenuUsage,
} from "client/jspPlatformExperiment";
import { SpacedAutocompleteInput } from "components/common/ui/SpacedTextInput";
import CommonButton from "components/common/ui/CommonButton";
import FoodUsageInfo from "components/food/ui/common/FoodUsageInfo";
import { InfoLabel } from "components/common/ui/InfoLabel";
import { useAppSelector } from "hooks/useReduxStore";
import foodItemOptionsSelector from "store/selectors/food/foodItemOptionsSelector";
import {
  filterFoodItemOptionsForSearch,
  getFoodItemOptionName,
} from "utils/food/foodItemOptionDisplayUtils";
import { isFoodRecipe } from "models/food/item/foodItem";

/** Cap preview lists so a heavily used food doesn't push the actions away. */
const PREVIEW_ITEMS_PER_SECTION = 5;

interface SwapFoodUsagesDialogProps {
  open: boolean;
  sourceFoodId: number;
  sourceFoodName: string;
  usedAsIngredientIn: FoodUsageAsIngredient[];
  usedInMenus: MenuUsage[];
  usedInMenuLoops: MenuLoopUsage[];
  isSwapping: boolean;
  errorMessage: string | null;
  /** When set, the dialog shows the swap result instead of the form. */
  report: FoodItemSwapUsagesReport | null;
  onCancel: () => void;
  onConfirm: (targetFood: FoodItem) => void;
  onCloseReport: () => void;
}

/**
 * Confirm-to-swap dialog: previews the source food's live usages, lets the
 * user pick the target food with the same option list as the ingredient step
 * editing autocomplete, then shows the per-category report of what the swap
 * rewrote.
 */
const SwapFoodUsagesDialog: FC<SwapFoodUsagesDialogProps> = ({
  open,
  sourceFoodId,
  sourceFoodName,
  usedAsIngredientIn,
  usedInMenus,
  usedInMenuLoops,
  isSwapping,
  errorMessage,
  report,
  onCancel,
  onConfirm,
  onCloseReport,
}) => {
  const allFoodItemOptions = useAppSelector(
    foodItemOptionsSelector.foodItemOptions,
  );
  const isFoodOptionsLoading = useAppSelector(
    foodItemOptionsSelector.isFoodOptionsLoading,
  );
  const targetFoodOptions = useMemo(
    () =>
      allFoodItemOptions.filter(
        (foodItemOpt) => foodItemOpt.id !== sourceFoodId,
      ),
    [allFoodItemOptions, sourceFoodId],
  );

  const [targetFood, setTargetFood] = useState<FoodItem | null>(null);

  // Reset the picked target when the dialog re-opens for a fresh swap.
  useEffect(() => {
    if (open) {
      setTargetFood(null);
    }
  }, [open]);

  const handleTargetChange = (
    _event: SyntheticEvent,
    newValue: FoodItem | null,
  ): void => {
    setTargetFood(newValue);
  };

  const hasLiveUsages =
    usedAsIngredientIn.length > 0 ||
    usedInMenus.length > 0 ||
    usedInMenuLoops.length > 0;
  const swappedAnything =
    !!report &&
    ((report.swapped_ingredient_usages?.length ?? 0) > 0 ||
      (report.swapped_menu_usages?.length ?? 0) > 0 ||
      (report.swapped_menu_loop_usages?.length ?? 0) > 0);

  return (
    <Dialog
      open={open}
      onClose={report ? onCloseReport : onCancel}
      fullWidth
      maxWidth="sm"
    >
      {report ? (
        <>
          <DialogTitle>Swap complete</DialogTitle>
          <DialogContent>
            <Typography variant="body2">
              Live usages of {report.source_food_name} (#
              {report.source_food_id}) were swapped to {report.target_food_name}{" "}
              (#{report.target_food_id}). The usages listed below were
              rewritten.
            </Typography>
            {swappedAnything ? (
              <FoodUsageInfo
                title="Swapped in"
                usedAsIngredientIn={report.swapped_ingredient_usages ?? []}
                usedInMenus={report.swapped_menu_usages ?? []}
                usedInMenuLoops={report.swapped_menu_loop_usages ?? []}
                maxItemsPerSection={PREVIEW_ITEMS_PER_SECTION}
              />
            ) : (
              <Typography variant="body2" sx={{ mt: 1 }}>
                No live usages needed swapping.
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <CommonButton text="Close" positive onClick={onCloseReport} />
          </DialogActions>
        </>
      ) : (
        <>
          <DialogTitle>Swap food usages</DialogTitle>
          <DialogContent>
            <Stack spacing={2}>
              <Typography variant="body2">
                Swap every live usage of {sourceFoodName} (#{sourceFoodId}) —
                ingredient steps of activated recipes, menu templates, future
                menus, and menus linked to an active or future menu loop — to
                the target food. If the target&apos;s own recipe uses this food
                as an ingredient, that usage is kept to avoid a self-reference.
                This food stays activated; deactivate it separately once nothing
                uses it anymore.
              </Typography>
              {hasLiveUsages ? (
                <FoodUsageInfo
                  title="Currently used in"
                  usedAsIngredientIn={usedAsIngredientIn}
                  usedInMenus={usedInMenus}
                  usedInMenuLoops={usedInMenuLoops}
                  maxItemsPerSection={PREVIEW_ITEMS_PER_SECTION}
                />
              ) : (
                <Typography variant="body2">
                  This food has no live usages right now.
                </Typography>
              )}
              <SpacedAutocompleteInput
                size="small"
                label="Target food"
                shrink
                disablePortal={false}
                loading={isFoodOptionsLoading}
                options={targetFoodOptions}
                getOptionLabel={(option) => getFoodItemOptionName(option)}
                getOptionKey={(option) => option.id}
                renderOption={(props, option) => {
                  const { ...optionProps } = props;
                  return (
                    <Box component="li" {...optionProps}>
                      {option.food_name}
                      <Tooltip title="ID">
                        <InfoLabel>Food ID: {option.id}</InfoLabel>
                      </Tooltip>
                      {isFoodRecipe(option) ? null : (
                        <Tooltip title="This option is an ingredient">
                          <InfoLabel>ING</InfoLabel>
                        </Tooltip>
                      )}
                    </Box>
                  );
                }}
                groupBy={(option) =>
                  isFoodRecipe(option) ? "Recipe" : "Ingredient"
                }
                isOptionEqualToValue={(option, value) => option.id === value.id}
                filterOptions={filterFoodItemOptionsForSearch}
                value={targetFood}
                onChange={handleTargetChange}
                error={!targetFood}
              />
              <Alert severity="warning">
                Swapping rewrites every live usage listed above and cannot be
                undone.
              </Alert>
              {errorMessage ? (
                <Alert severity="error">{errorMessage}</Alert>
              ) : null}
            </Stack>
          </DialogContent>
          <DialogActions>
            <CommonButton
              text="Cancel"
              onClick={onCancel}
              disabled={isSwapping}
            />
            <CommonButton
              text={isSwapping ? "Swapping..." : "Swap permanently"}
              danger={!!targetFood && !isSwapping}
              disabled={!targetFood || isSwapping}
              onClick={() => {
                if (targetFood) {
                  onConfirm(targetFood);
                }
              }}
            />
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};

export default SwapFoodUsagesDialog;
